import { Widget } from '../core/Widget.js';

/**
 * Виджет масочного ввода (input/textarea с маской).
 * Без внутреннего `_value`: «сырой» ввод вычисляется из `element.value` по маске.
 */
class MaskWidget extends Widget {
    constructor() {
        super(...arguments);
        this._handlers = {};
        this._maskTokens = [];
        this._hasRecursive = false;
        this._recursiveIndex = -1;
        /** Backspace по статике — просто сдвигаем курсор влево. */
        this._handleKeyDown = (event) => {
            if (event.key !== 'Backspace')
                return;
            const { selectionStart, selectionEnd } = this.element;
            if (selectionStart === null || selectionEnd === null)
                return;
            if (selectionStart !== selectionEnd)
                return;
            const staticChars = this._maskTokens
                .filter(t => t.type === 'static')
                .map(t => t.char)
                .join('');
            const charBefore = this.element.value[selectionStart - 1];
            if (charBefore && staticChars.includes(charBefore)) {
                event.preventDefault();
                const newCursorPos = selectionStart - 1;
                this.element.setSelectionRange(newCursorPos, newCursorPos);
            }
        };
        /** На ввод — пересчитываем raw из текущего `element.value` и перерисовываем. */
        this._handleInput = (_event) => {
            this.applyMask(false);
            this._setCursorPosition();
        };
        /** На blur — если есть неполные позиции, заполняем плейсхолдером. */
        this._handleBlur = () => {
            if (this.getRawValue().length > 0) {
                this.applyMask(true);
            }
        };
        /** На focus — нормализуем и ставим курсор. */
        this._handleFocus = () => {
            this.applyMask(false);
            this._setCursorPosition();
        };
        //#endregion
    }
    //#region Жизненный цикл
    _create(options) {
        if (!options.mask || options.mask.trim() === "") {
            throw new Error("[MaskWidget] Ошибка инициализации: не передан обязательный параметр mask");
        }
        this.mask = options.mask;
        this.placeholder = options.placeholder ?? '_';
        this._buildMaskTokens(options);
        if (options.onInput)
            this.on('input', options.onInput);
        this.detachEvents();
        this.attachEvents();
        // важное требование: defaultValue ставим ТОЛЬКО если поле пустое
        const isEmpty = (this.element.value ?? '').length === 0;
        if (isEmpty && options.defaultValue != null && options.defaultValue !== '') {
            // принимаем defaultValue как «сырой» ввод (без статичных символов)
            this.applyMask(false, options.defaultValue);
        }
        else {
            // просто нормализуем текущее значение поля по маске
            this.applyMask(false);
        }
    }
    _onDestroy() {
        this.detachEvents();
        this._handlers = {};
    }
    //#endregion
    //#region Публичные API
    /** Обновляет опции (изменяет только переданные поля). */
    update(options) {
        if (options.mask !== undefined)
            this.mask = options.mask;
        if (options.placeholder !== undefined)
            this.placeholder = options.placeholder;
        if (options.mask !== undefined || options.tokens !== undefined) {
            this._buildMaskTokens({
                mask: this.mask,
                placeholder: this.placeholder,
                defaultValue: undefined,
                onInput: undefined,
                tokens: options.tokens ?? undefined
            });
        }
        if (options.onInput)
            this.on('input', options.onInput);
        if (options.defaultValue !== undefined) {
            // В update меняем значение ТОЛЬКО если явно пришёл defaultValue
            this.applyMask(false, options.defaultValue);
        }
        else {
            this.applyMask(false);
        }
    }
    /** Устанавливает «сырой» ввод (без статичных символов) и применяет маску. */
    set(rawValue) {
        this.applyMask(false, rawValue ?? '');
    }
    /** Возвращает форматированное значение (со статикой маски). */
    get() {
        return this.element.value;
    }
    /** Возвращает «сырой» ввод (без статичных символов маски). */
    getRawValue() {
        return this._extractRawByMask(this.element.value ?? '');
    }
    /** Делает поле доступным для редактирования */ enable() { this.element.disabled = false; }
    /** Делает поле недоступным для редактирования */ disable() { this.element.disabled = true; }
    /** Показывает элемент */ show() { this.element.style.display = ''; }
    /** Скрывает элемент */ hide() { this.element.style.display = 'none'; }
    //#endregion
    //#region События DOM
    attachEvents() {
        this.element.addEventListener('keydown', this._handleKeyDown);
        this.element.addEventListener('input', this._handleInput);
        this.element.addEventListener('blur', this._handleBlur);
        this.element.addEventListener('focus', this._handleFocus);
    }
    detachEvents() {
        this.element.removeEventListener('keydown', this._handleKeyDown);
        this.element.removeEventListener('input', this._handleInput);
        this.element.removeEventListener('blur', this._handleBlur);
        this.element.removeEventListener('focus', this._handleFocus);
    }
    //#endregion
    //#region Маскирование/форматирование
    _buildMaskTokens(options) {
        const custom = options.tokens ?? {};
        const resolveCustom = (ch) => {
            const t = custom[ch];
            if (!t)
                return null;
            if (t instanceof RegExp)
                return { pattern: t, recursive: false };
            return { pattern: t.pattern, recursive: !!t.recursive };
        };
        this._maskTokens = [];
        this._hasRecursive = false;
        this._recursiveIndex = -1;
        for (let i = 0; i < this.mask.length; i++) {
            const ch = this.mask[i];
            if (ch === '9') {
                this._maskTokens.push({ type: 'token', pattern: /\d/, recursive: false });
                continue;
            }
            if (ch === 'a') {
                this._maskTokens.push({ type: 'token', pattern: /[A-Za-z]/, recursive: false });
                continue;
            }
            if (ch === '*') {
                this._maskTokens.push({ type: 'token', pattern: /[\s\S]/, recursive: false });
                continue;
            }
            const ct = resolveCustom(ch);
            if (ct) {
                if (ct.recursive) {
                    this._hasRecursive = true;
                    this._recursiveIndex = this._maskTokens.length;
                }
                this._maskTokens.push({ type: 'token', pattern: ct.pattern, recursive: !!ct.recursive });
                continue;
            }
            this._maskTokens.push({ type: 'static', char: ch });
        }
        if (this._hasRecursive && this._recursiveIndex !== this._maskTokens.length - 1) {
            console.warn('[MaskWidget] Рекурсивный токен поддерживается только в конце маски. Игнорирую рекурсию.');
            this._hasRecursive = false;
            this._recursiveIndex = -1;
        }
    }
    /**
     * Применяет маску.
     * @param isBlurEvent   если true — заполняем плейсхолдеры на пустых позициях
     * @param rawOverride   «сырой» ввод, если нужно явно задать (например, defaultValue)
     */
    applyMask(isBlurEvent = false, rawOverride) {
        // если явно не передали «сырой» ввод — восстановим его из текущего element.value
        const raw = rawOverride ?? this._extractRawByMask(this.element.value ?? '');
        let out = '';
        let rawIdx = 0;
        for (let i = 0; i < this._maskTokens.length; i++) {
            const tk = this._maskTokens[i];
            if (tk.type === 'static') {
                out += tk.char;
                continue;
            }
            if (!tk.recursive) {
                if (rawIdx < raw.length) {
                    out += raw[rawIdx++];
                }
                else if (isBlurEvent) {
                    out += this.placeholder;
                }
                else {
                    break;
                }
                continue;
            }
            // рекурсивный последний токен — добираем всё, что подходит
            while (rawIdx < raw.length) {
                const ch = raw[rawIdx];
                if (tk.pattern.test(ch)) {
                    out += ch;
                    rawIdx++;
                }
                else {
                    // неподходящие символы пропускаем
                    rawIdx++;
                }
            }
            if (isBlurEvent && out.length === 0) {
                out += this.placeholder;
            }
            break; // рекурсивный — всегда последний
        }
        this.element.value = out;
        this._fire('input', out);
    }
    /**
     * Восстанавливает «сырой» ввод из произвольной строки (обычно `element.value`),
     * соблюдая последовательность токенов; рекурсивный последний токен — собирает «хвост».
     */
    _extractRawByMask(inputVal) {
        let raw = '';
        let maskIdx = 0;
        for (let i = 0; i < inputVal.length; i++) {
            const ch = inputVal[i];
            // прокручиваем статику маски
            while (maskIdx < this._maskTokens.length && this._maskTokens[maskIdx].type === 'static') {
                const st = this._maskTokens[maskIdx];
                if (ch === st.char) {
                    maskIdx++;
                    // оставим ch на проверку следующим токеном
                    break;
                }
                else {
                    maskIdx++;
                    continue;
                }
            }
            if (maskIdx >= this._maskTokens.length)
                break;
            const tk = this._maskTokens[maskIdx];
            if (tk.type === 'token') {
                if (tk.recursive) {
                    if (tk.pattern.test(ch)) {
                        raw += ch;
                    }
                    // остаёмся на этом токене
                }
                else {
                    if (tk.pattern.test(ch)) {
                        raw += ch;
                        maskIdx++;
                    }
                    // если не подошло — просто игнорируем символ
                }
            }
        }
        return raw;
    }
    /** Ставит курсор на первую позицию плейсхолдера или в конец. */
    _setCursorPosition() {
        const pos = this.element.value.indexOf(this.placeholder);
        const cursorPos = pos !== -1 ? pos : this.element.value.length;
        this.element.setSelectionRange(cursorPos, cursorPos);
    }
    //#endregion
    //#region Событийная шина
    on(event, handler) {
        if (!this._handlers[event])
            this._handlers[event] = [];
        this._handlers[event].push(handler);
    }
    off(event, handler) {
        if (!this._handlers[event])
            return;
        if (!handler) {
            this._handlers[event] = [];
            return;
        }
        this._handlers[event] = this._handlers[event].filter(h => h !== handler);
    }
    _fire(event, value) {
        if (this._handlers[event]) {
            this._handlers[event].forEach(handler => handler(value));
        }
    }
}
/** Имя виджета */
MaskWidget._alias = "Mask";

export { MaskWidget };
